import { Injectable, Injector, NgModuleRef, Type, createNgModule, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { LegalDocument, LegalDocumentType } from 'src/app/interfaces';
import { HttpService } from './http.service';
import type { DocumentViewerModule } from 'src/app/shared/document-viewer/document-viewer.module';

const DIALOG_CONFIG = {
  width: '100vw',
  maxWidth: '100vw',
  height: '100vh',
  maxHeight: '100vh',
};

/** The module reference supplies the dialog's injector; the component names what to open in it. */
interface LoadedViewer {
  ref: NgModuleRef<DocumentViewerModule>;
  component: Type<unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class LegalDocumentsService {
  private cache = new Map<string, Promise<LegalDocument>>();
  private readonly injector = inject(Injector);
  private viewer: Promise<LoadedViewer> | null = null;

  constructor(
    private dialog: MatDialog,
    private http: HttpService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
  ) { }

  /**
   * Bring up the viewer's module, once per tab.
   *
   * It carries `ngx-markdown` and owns the `MarkdownService` the renderer resolves, so the module
   * reference is kept rather than rebuilt per document: a second instance would mean a second
   * renderer for no gain. Held as the promise, so two documents opened in quick succession join
   * one load instead of racing two.
   */
  private async loadViewer(): Promise<LoadedViewer | null> {
    this.viewer ??= import('src/app/shared/document-viewer/document-viewer.module').then(m => ({
      ref: createNgModule(m.DocumentViewerModule, this.injector),
      component: m.DocumentViewerComponent,
    }));
    try {
      return await this.viewer;
    } catch (error) {
      // Clearing the field is the point of catching here: a rejected promise left in it would make
      // every later open fail without another request ever going out, so one flaky chunk load would
      // cost the tab its legal documents.
      this.viewer = null;
      console.error('Failed to load the legal document viewer.', error);
      this.reportLoadFailure();
      return null;
    }
  }

  /** Tell the user the document could not be shown. The only failure surface this service has. */
  private reportLoadFailure(): void {
    this.snackBar.open(
      this.translate.instant('components.document_viewer.load_error'),
      this.translate.instant('common.buttons.close'),
      { duration: 5000 }
    );
  }

  /**
   * Open a legal document, resolving with what was displayed, or `null` when it could not be.
   *
   * Pass `expectedVersion` wherever the caller is about to record consent. The cache below lives
   * for the whole tab, so without it a user could be shown a superseded text and then asked to
   * accept the current version number — recording agreement to something they never read. Naming
   * the version forces a fresh fetch, which costs one request on a screen visited rarely and is
   * the only way to be sure the text on screen is the text being consented to.
   *
   * **Never rejects.** This service already owns the failure — it closes the dialog and raises the
   * snackbar — and every caller opens a document for the user to read rather than for a value it
   * needs, so a rejection would hand each of them a promise they have no use for and produce an
   * unhandled rejection wherever they drop it. A caller that wants the version checks for `null`.
   */
  private async openDocument(
    type: LegalDocumentType,
    expectedVersion?: string
  ): Promise<LegalDocument | null> {
    const lang = this.translate.currentLang;
    const cacheKey = `${type}-${lang}`;
    if (expectedVersion) {
      this.cache.delete(cacheKey);
      this.http.clearLegalDocumentCache(type, lang);
    }
    if (!this.cache.has(cacheKey)) {
      this.cache.set(
        cacheKey,
        this.http.getLegalDocument(type, lang).then((response) => {
          if (!response?.successful || !response.data) {
            throw new Error(`Failed to load legal document (${cacheKey})`);
          }
          return response.data;
        })
      );
    }
    const documentPromise = this.cache.get(cacheKey)!;
    let dialogRef: MatDialogRef<unknown> | null = null;
    let failed = false;
    // Attached before anything is awaited: the fetch is already in flight, and a rejection with no
    // handler on it is an unhandled rejection even though one is attached a few lines later. This
    // is also what this method hands back, so the rejection is answered exactly once and no caller
    // is left holding a promise it has to catch.
    const settled = documentPromise.catch((err) => {
      failed = true;
      console.error(`Failed to load legal document (${cacheKey}):`, err);
      this.cache.delete(cacheKey);
      dialogRef?.close();
      this.reportLoadFailure();
      return null;
    });
    // The fetch above and the renderer's chunk load in parallel; only the dialog waits on the
    // chunk, and it carries the fetch's loading state itself.
    const viewer = await this.loadViewer();
    // A fetch that failed while the chunk loaded has already reported itself. Opening now would put
    // an empty dialog over the message. A viewer that failed to load has reported itself too, and
    // there is nothing to open the document in.
    if (viewer !== null && !failed) {
      dialogRef = this.dialog.open(viewer.component, {
        ...DIALOG_CONFIG,
        injector: viewer.ref.injector,
        data: { documentPromise },
      });
    }
    return settled;
  }

  openTerms(expectedVersion?: string): Promise<LegalDocument | null> {
    return this.openDocument(LegalDocumentType.TNC, expectedVersion);
  }

  openDataPrivacyPolicy(expectedVersion?: string): Promise<LegalDocument | null> {
    return this.openDocument(LegalDocumentType.PRIVACY_POLICY, expectedVersion);
  }
}
